import React, { useRef, useState } from 'react';
import renderIf from 'render-if';
import classnames from 'classnames';
import { DateInput } from '@aha-app/react-in-place';
import moment from 'moment';
import styles from '../Dashboards.module.css';
import Panel from './Panel';
import ReleaseSelectro from './ReleaseSelectro';
import Immutable from 'immutable';
import ExtensionPanelConfiguration from './ExtensionPanelConfiguration';
import {
  contextToName,
  bookmarkURL,
  showLegendOption,
  supportsResizableContent,
} from '../helpers';
import { type DashboardPanelContextType } from '../constants';

const forPreview = panelConfig => {
  if (panelConfig.isHeader) {
    return panelConfig.merge({
      width: 2,
      height: 1,
      column: 1,
      row: 1,
    });
  }
  return panelConfig.merge({
    width: 2,
    height: 3,
    column: 1,
    row: 1,
  });
};

interface PanelConfigurationProps {
  panel: any;
  updatePanel: (panel: any) => void;
  context: DashboardPanelContextType;
}

const PanelConfiguration: React.FC<PanelConfigurationProps> = ({
  panel,
  updatePanel,
  context,
}) => {
  const setPanelRelease = ({ text, value }) => {
    let release = Immutable.Map({
      name: text,
      id: value,
    });

    updatePanel(panel.set('release', release));
  };
  const [previewConfig, setPreviewConfig] = useState(forPreview(panel));
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  const updatePreviewAndPanel = update => {
    setPreviewConfig(forPreview(update));
    updatePanel(update);
  };

  const activateDatePicker = ref => {
    ref.current.activate(ref.current);
  };

  const updateBookmarkDate = (key, date) => {
    const momentDate = moment(date);
    updatePreviewAndPanel(
      panel.set(
        key,
        momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null
      )
    );
  };

  const initialBookmarkDate = date => {
    const momentDate = moment(date);
    return momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
  };

  const tooltipCopy = panel.disabledFilters
    ? 'This panel type is not compatible with dashboard filters'
    : 'If filters have been added to the dashboard they will override the filters on this panel. Uncheck this option to use the panel filters instead.';

  const showRelease = panel.type === 'Bookmarks::ReleaseProgress';

  return (
    <>
      <div className={styles.ModalLayout}>
        <div className={styles.PanelConfigurationSidebar}>
          {renderIf(
            panel.type !== 'ExtensionPanel' &&
              panel.type !== 'SectionHeaderPanel'
          )(() => (
            <div className={styles.PanelConfigurationSection}>
              <h2 className={styles.PanelConfigurationSectionHeader}>
                Data configuration
              </h2>
              <div className={styles.PanelConfigurationData}>
                {`The data in this ${contextToName(
                  context
                )} is based on a report`}
                <br />
                <a href={bookmarkURL(panel)} target='_blank'>
                  Customize report{' '}
                  <i className='fa-regular fa-angle-right' aria-hidden='true' />
                </a>
              </div>
              {renderIf(showRelease)(() => (
                <ReleaseSelectro
                  select={setPanelRelease}
                  release={panel.release}
                />
              ))}

              {renderIf(context !== 'note' && !showRelease)(() => (
                <div
                  className={classnames(
                    styles.PanelConfigurationLayout,
                    'mt-3'
                  )}
                >
                  <label>
                    <input
                      type='checkbox'
                      aria-label='Use dashboard filters'
                      className={styles.PanelConfigurationCheckboxInput}
                      disabled={panel.disabledFilters}
                      checked={Boolean(panel.dashboardFilter)}
                      onChange={e => {
                        updatePanel(
                          panel.set('dashboardFilter', e.target.checked)
                        );
                      }}
                    />
                    <span className={styles.PanelConfigurationCheckboxSpan}>
                      Use dashboard filters{' '}
                      <i
                        className='fa-regular fa-circle-question'
                        data-title={tooltipCopy}
                      />
                    </span>
                  </label>
                </div>
              ))}
            </div>
          ))}
          <div className={styles.PanelConfigurationSection}>
            <h2 className={styles.PanelConfigurationSectionHeader}>
              Panel layout
            </h2>
            <div className={styles.PanelConfigurationLayout}>
              <div className={styles.PanelConfigurationSectionSubtitle}>
                Title
              </div>
              <input
                type='text'
                value={panel.name}
                aria-label={`${contextToName(context, false, true)} title`}
                onChange={e => {
                  updatePreviewAndPanel(panel.set('name', e.target.value));
                }}
              />
            </div>
            {renderIf(context === 'note')(() => (
              <div className={styles.PanelConfigurationLayout}>
                <div className={styles.PanelConfigurationSectionSubtitle}>
                  Options
                </div>
                <label>
                  <input
                    type='checkbox'
                    checked={panel.fullWidth}
                    aria-label={`Show full size`}
                    onChange={e => {
                      updatePreviewAndPanel(
                        panel.set('fullWidth', e.target.checked)
                      );
                    }}
                  />{' '}
                  <span className={styles.PanelConfigurationCheckboxSpan}>
                    Show panel full size
                  </span>
                </label>
              </div>
            ))}
            {renderIf(
              context === 'note' &&
                panel.fullWidth &&
                supportsResizableContent(panel)
            )(() => (
              <div className={styles.PanelConfigurationLayout}>
                <label>
                  <input
                    type='checkbox'
                    checked={panel.get('resizeToFit')}
                    aria-label={`Resize content to fit`}
                    onChange={e => {
                      updatePreviewAndPanel(
                        panel.set('resizeToFit', e.target.checked)
                      );
                    }}
                  />{' '}
                  <span className={styles.PanelConfigurationCheckboxSpan}>
                    Resize content to fit (publishing only)
                  </span>
                </label>
              </div>
            ))}
            {renderIf(panel.type === 'Bookmarks::CalendarReport')(() => (
              <div className={styles.PanelConfigurationLayout}>
                <div className={styles.PanelConfigurationSectionSubtitle}>
                  Date range
                </div>
                <div className='date-fields-wrapper'>
                  <div className='date-fields-item'>
                    <div className='react-in-place-wrapper react-in-place-wrapper--inline'>
                      <DateInput
                        ref={startDateRef}
                        class='react-in-place__date react-in-place react-in-place--inline react-in-place__placeholder react-in-place--empty'
                        dateFormat='YYYY-MM-DD'
                        value={initialBookmarkDate(panel.bookmarkStartDate)}
                        onChange={date =>
                          updateBookmarkDate('bookmarkStartDate', date)
                        }
                      />
                      <button onClick={() => activateDatePicker(startDateRef)}>
                        <i className='fa-solid fa-calendar' />
                      </button>
                    </div>
                  </div>
                  <div className='date-fields-divider'>&mdash;</div>
                  <div className='date-fields-item'>
                    <div className='react-in-place-wrapper react-in-place-wrapper--inline'>
                      <DateInput
                        ref={endDateRef}
                        class='react-in-place__date react-in-place react-in-place--inline react-in-place__placeholder react-in-place--empty'
                        dateFormat='YYYY-MM-DD'
                        value={initialBookmarkDate(panel.bookmarkEndDate)}
                        onChange={date =>
                          updateBookmarkDate('bookmarkEndDate', date)
                        }
                      />
                      <button onClick={() => activateDatePicker(endDateRef)}>
                        <i className='fa-solid fa-calendar' />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {renderIf(showLegendOption(panel.type))(() => (
              <div
                className={classnames(styles.PanelConfigurationLayout, 'mt-3')}
              >
                <label>
                  <input
                    type='checkbox'
                    aria-label={`${contextToName(
                      context,
                      false,
                      true
                    )} show legend`}
                    className={styles.PanelConfigurationCheckboxInput}
                    checked={Boolean(panel.showLegend)}
                    onChange={e => {
                      updatePanel(panel.set('showLegend', e.target.checked));
                    }}
                  />
                  <span className={styles.PanelConfigurationCheckboxSpan}>
                    Show legend
                  </span>
                </label>
              </div>
            ))}
          </div>
          {renderIf(panel.type === 'ExtensionPanel')(
            <ExtensionPanelConfiguration
              panel={panel}
              updatePanel={updatePreviewAndPanel}
            />
          )}
        </div>
        <div className={styles.PanelConfigurationPreview}>
          <h2>Preview</h2>

          <div className={styles.PanelConfigurationPreviewGrid}>
            {/* @ts-expect-error Panel is not properly typed */}
            <Panel panel={previewConfig} preview />
          </div>
        </div>
      </div>
    </>
  );
};

export default PanelConfiguration;
